import { parseReportCitations, ParsedCitation, findCitationsWithNestedBrackets } from "./parseCitations";

export interface ExportSection {
  id: string;
  name: string;
  description: string | null;
  description_ai_output?: string | null;
  priming: string | null;
  parent_id: string | null;
  sources?: { document_ids: string[]; extraction_point_ids?: string[] } | null;
}

export interface ExtractionTableRecord {
  documentId: string;
  data: string | null;
}

export interface ExtractionTableColumn {
  id: string;
  name: string;
}

export interface ExtractionTableOptions {
  records: ExtractionTableRecord[];
  columns: ExtractionTableColumn[];
  documents: ExportDocument[];
}

/**
 * Builds a markdown table of contents from the sections list.
 * Root sections are plain paragraphs; child sections use blockquote syntax for indentation.
 */
export function buildTableOfContents(sections: ExportSection[]): string {
  const lines: string[] = ["## Contents", ""];
  const nums = buildSectionNumberMap(sections);

  for (const section of sections) {
    const num = nums[section.id];
    if (!section.parent_id) {
      // Escape the dot so "1." is not parsed as an ordered list marker
      lines.push(`${num}\\. ${section.name}`, "");
    } else {
      // Blockquote syntax produces reliable paragraph indentation in the Word parser
      lines.push(`> ${num}. ${section.name}`, "");
    }
  }

  lines.push("---", "");
  return lines.join("\n");
}

/**
 * Raw <h2>/<h3> instead of "##"/"###" so the section id can ride along in
 * data-heading-section-id. The generation service reads that attribute and
 * tags the heading with it, so the Word add-in gets a pre-tagged document
 * with no add-in changes needed. Other callers never send the attribute,
 * so this is purely additive for them.
 */
function buildSectionHeadingLine(section: ExportSection, num: string): string {
  const level = section.parent_id ? "h3" : "h2";
  const id = _htmlEscape(section.id);
  const text = _htmlEscape(section.name);
  return `<${level} data-heading-section-id="${id}">${num}. ${text}</${level}>`;
}

export function buildSectionNumberMap(sections: ExportSection[]): Record<string, string> {
  const map: Record<string, string> = {};
  let rootIndex = 0;
  const childCounters: Record<string, number> = {};

  for (const section of sections) {
    if (!section.parent_id) {
      rootIndex += 1;
      childCounters[section.id] = 0;
      map[section.id] = `${rootIndex}`;
    } else {
      childCounters[section.parent_id] = (childCounters[section.parent_id] ?? 0) + 1;
      map[section.id] = `${rootIndex}.${childCounters[section.parent_id]}`;
    }
  }
  return map;
}

export interface ExportDocument {
  id: string;
  filename: string;
  title: string;
  author: string;
  year: string;
  sourceOrganisation: string;
}

function resolveDocumentNames(ids: string[], documents: ExportDocument[]): string {
  return ids
    .map((id) => {
      const doc = documents.find((d) => d.id === id);
      return doc ? (doc.title || doc.filename) : null;
    })
    .filter(Boolean)
    .join(", ");
}

/**
 * Builds a markdown string representing the report outline structure —
 * section names, descriptions, sources, and AI instructions.
 */
export function buildOutlineMarkdown(
  templateName: string,
  sections: ExportSection[],
  documents: ExportDocument[]
): string {
  const lines: string[] = [];
  const nums = buildSectionNumberMap(sections);

  for (const section of sections) {
    const num = nums[section.id];
    lines.push(buildSectionHeadingLine(section, num), "");

    const descriptionText = section.description_ai_output || section.description;
    if (descriptionText) {
      lines.push(`**Description:** ${descriptionText}`, "");
    }

    const docIds = section.sources?.document_ids ?? [];
    if (docIds.length > 0) {
      const names = resolveDocumentNames(docIds, documents);
      if (names) {
        lines.push(`**Sources:** ${names}`, "");
      }
    }

    if (!section.parent_id) {
      lines.push("---", "");
    }
  }

  return lines.join("\n");
}

/**
 * Builds a markdown string from generated report content.
 * Sections without generated content are omitted.
 * An AMA-formatted reference list is appended after each section that has citations.
 */
export function buildReportMarkdown(
  templateName: string,
  sections: ExportSection[],
  generatedContent: Record<string, string>,
  documents: ExportDocument[] = []
): string {
  const lines: string[] = [];
  const nums = buildSectionNumberMap(sections);

  for (const section of sections) {
    const raw = generatedContent[section.id];
    if (!raw) continue;

    const { text: parsedText, citations } = parseReportCitations(raw);
    const num = nums[section.id];
    lines.push(buildSectionHeadingLine(section, num), "");
    lines.push(parsedText, "");

    // Per-section AMA reference list.
    // Wrapped in <small> so the Word parser renders them at reference_size_pt.
    // "References" label is bold+small rather than a heading style.
    if (citations.length > 0) {
      lines.push("<small><strong>References</strong></small>", "");
      for (const c of citations) {
        const doc = _findDocument(documents, c.filename);
        const ref = _htmlEscape(_formatAmaReference(c.number, c.filename, c.page, doc));
        lines.push(`<small>${ref}</small>`, "");
      }
    }

    if (!section.parent_id) {
      lines.push("---", "");
    }
  }

  return lines.join("\n");
}

interface _TableResult {
  markdown: string;
  citations: Array<{ number: number; filename: string; page: string }>;
}

function _buildSectionExtractionTable(
  section: ExportSection,
  options: ExtractionTableOptions,
  startIndex: number,
): _TableResult {
  const empty: _TableResult = { markdown: "", citations: [] };
  const { records, columns, documents } = options;
  const selectedDocIds = section.sources?.document_ids ?? [];
  const selectedEpIds = section.sources?.extraction_point_ids ?? [];

  let filteredRecords: ExtractionTableRecord[];
  if (selectedDocIds.length > 0) {
    filteredRecords = records.filter((r) => selectedDocIds.includes(r.documentId) && r.data);
  } else if (selectedEpIds.length > 0) {
    const projectDocIds = new Set(documents.map((d) => d.id));
    filteredRecords = records.filter((r) => r.data && projectDocIds.has(r.documentId));
  } else {
    return empty;
  }

  if (filteredRecords.length === 0) return empty;

  const filteredColumns = selectedEpIds.length > 0
    ? columns.filter((c) => selectedEpIds.includes(c.id))
    : columns;

  if (filteredColumns.length === 0) return empty;

  // Citation registry continues from where the section text left off.
  const citationRegistry = new Map<string, { number: number; filename: string; page: string }>();
  let citationCounter = startIndex;

  function processCell(raw: string): string {
    const matches = findCitationsWithNestedBrackets(raw);
    if (matches.length === 0) return raw.replace(/\|/g, "\\|").replace(/\n/g, " ").trim();

    let result = raw;
    let offset = 0;
    for (const { content, start, end } of matches) {
      const citationText = (content.split("||")[0] ?? "");
      const filename = (citationText.split("#")[0] ?? "").replace(/\\(.)/g, "$1");
      let page = "";
      const pageMatch = /#page=(\d+)/.exec(citationText);
      if (pageMatch) page = `#page=${pageMatch[1]}`;
      else if (citationText.includes("#")) page = citationText.substring(citationText.indexOf("#"));

      const key = `${filename}|${page}`;
      let entry = citationRegistry.get(key);
      if (!entry) {
        entry = { number: citationCounter++, filename, page };
        citationRegistry.set(key, entry);
      }

      const adjustedStart = start - offset;
      const adjustedEnd = end - offset;
      const hasPrecedingBackslash = adjustedStart > 0 && result[adjustedStart - 1] === "\\";
      const effectiveStart = hasPrecedingBackslash ? adjustedStart - 1 : adjustedStart;
      const placeholder = `<sup>${entry.number}</sup>`;
      result = result.substring(0, effectiveStart) + placeholder + result.substring(adjustedEnd);
      offset += (adjustedEnd - effectiveStart) - placeholder.length;
    }
    return result.replace(/\|/g, "\\|").replace(/\n/g, " ").trim();
  }

  const escapeHeader = (s: string) => s.replace(/\|/g, "\\|").trim();

  const headers = ["Study Name", ...filteredColumns.map((c) => c.name)];
  const rows: string[][] = [];

  for (const record of filteredRecords) {
    const doc = documents.find((d) => d.id === record.documentId);
    const studyName = doc ? (doc.title || doc.filename) : record.documentId;
    try {
      const parsed = JSON.parse(record.data!) as Record<string, unknown>;
      const picoByName: Record<string, string | undefined> = {
        population: typeof parsed.population === "string" ? parsed.population : undefined,
        intervention: typeof parsed.intervention === "string" ? parsed.intervention : undefined,
        comparator: typeof parsed.comparator === "string" ? parsed.comparator : undefined,
        outcome: typeof parsed.outcome === "string" ? parsed.outcome : undefined,
      };
      const fields = (parsed.fields ?? {}) as Record<string, { value?: string } | string>;
      const values = filteredColumns.map((col) => {
        const field = fields[col.name];
        const raw = field
          ? (typeof field === "string" ? field : (field.value ?? ""))
          : (picoByName[col.name.toLowerCase()] ?? "");
        return processCell(raw);
      });
      rows.push([escapeHeader(studyName), ...values]);
    } catch {
      rows.push([escapeHeader(studyName), ...filteredColumns.map(() => "")]);
    }
  }

  if (rows.length === 0) return empty;

  const sep = headers.map(() => "---");
  const markdown = [
    `| ${headers.map(escapeHeader).join(" | ")} |`,
    `| ${sep.join(" | ")} |`,
    ...rows.map((row) => `| ${row.join(" | ")} |`),
  ].join("\n");

  const citations = [...citationRegistry.values()].sort((a, b) => a.number - b.number);
  return { markdown, citations };
}

export function buildSectionExtractionTableMarkdown(
  section: { sources?: { document_ids: string[]; extraction_point_ids?: string[] } | null },
  options: ExtractionTableOptions,
  startIndex = 1,
): string {
  return _buildSectionExtractionTable(section as ExportSection, options, startIndex).markdown;
}

/**
 * Basic citation variant.
 * In-text: numbered superscripts ^1^ ^2^ from parseReportCitations.
 * Per-section references: all on one line at 9 pt, comma-separated,
 * formatted as "[1] filename, p. X, [2] filename, p. Y".
 * Uses <small> so the backend converts it to a 9 pt paragraph.
 */
export function buildReportMarkdownBasicCitations(
  templateName: string,
  sections: ExportSection[],
  generatedContent: Record<string, string>,
  extractionTableOptions?: ExtractionTableOptions,
  /** Artefact markdown per section id, placed after the text and table. Artefacts
   *  aren't saved in the section text, so they're supplied separately. */
  artifactsBySection?: Record<string, string>,
): string {
  const lines: string[] = [];
  const nums = buildSectionNumberMap(sections);

  for (const section of sections) {
    const raw = generatedContent[section.id];
    if (!raw) continue;

    const { text: parsedText, citations } = parseReportCitations(raw);
    const num = nums[section.id];
    lines.push(buildSectionHeadingLine(section, num), "");
    lines.push(parsedText, "");

    let allCitations = citations;
    if (extractionTableOptions) {
      const { markdown: tableMarkdown, citations: tableCitations } =
        _buildSectionExtractionTable(section, extractionTableOptions, citations.length + 1);
      if (tableMarkdown) lines.push(tableMarkdown, "");
      if (tableCitations.length > 0) allCitations = [...citations, ...tableCitations];
    }

    const artifactMarkdown = artifactsBySection?.[section.id];
    if (artifactMarkdown) lines.push(artifactMarkdown, "");

    if (allCitations.length > 0) {
      const refs = allCitations
        .map((c) => {
          const pageStr = _cleanPage(c.page);
          return pageStr
            ? `[${c.number}] ${c.filename}, p. ${pageStr}`
            : `[${c.number}] ${c.filename}`;
        })
        .join(", ");
      lines.push(`**References** ${refs}`, "");
    }

    if (!section.parent_id) {
      lines.push("---", "");
    }
  }

  return lines.join("\n");
}

/**
 * Simple variant of buildReportMarkdown.
 * Uses plain markdown for references — no HTML tags, no font-size tricks.
 * References appear per section as a bold "References" label followed by
 * a numbered plain-text list in AMA style.
 */
export function buildReportMarkdownSimple(
  templateName: string,
  sections: ExportSection[],
  generatedContent: Record<string, string>,
  documents: ExportDocument[] = []
): string {
  const lines: string[] = [];
  const nums = buildSectionNumberMap(sections);

  for (const section of sections) {
    const raw = generatedContent[section.id];
    if (!raw) continue;

    const { text: parsedText, citations } = parseReportCitations(raw);
    const num = nums[section.id];
    lines.push(buildSectionHeadingLine(section, num), "");
    lines.push(parsedText, "");

    if (citations.length > 0) {
      lines.push("**References**", "");
      for (const c of citations) {
        const doc = _findDocument(documents, c.filename);
        lines.push(_formatAmaReference(c.number, c.filename, c.page, doc), "");
      }
    }

    if (!section.parent_id) {
      lines.push("---", "");
    }
  }

  return lines.join("\n");
}

/** Returns just the base filename, lower-cased, for comparison. */
function _normalizeFilename(s: string): string {
  return s.replace(/^.*[\\/]/, "").toLowerCase().trim();
}

/**
 * Finds a document whose filename matches the citation filename.
 * Tries exact match first, then falls back to case-insensitive basename comparison.
 */
function _findDocument(documents: ExportDocument[], citationFilename: string): ExportDocument | undefined {
  // Exact match
  const exact = documents.find((d) => d.filename === citationFilename);
  if (exact) return exact;
  // Normalised match (strip path, lowercase)
  const norm = _normalizeFilename(citationFilename);
  return documents.find((d) => _normalizeFilename(d.filename) === norm);
}

/**
 * Extracts a human-readable page number from a raw citation page string.
 * Handles "#page=11", "page=11", "#11", and plain "11".
 */
function _cleanPage(raw: string): string {
  if (!raw) return "";
  // "#page=11" or "page=11" (case-insensitive)
  const m = /page=(\d+)/i.exec(raw);
  if (m) return m[1];
  // "#11" or plain "11"
  const n = /(\d+)/.exec(raw);
  return n ? n[1] : "";
}

/**
 * Formats a single AMA-style reference entry.
 * AMA numeric format: Author. Title. Organisation; Year. p. X.
 * Falls back to filename when document metadata is unavailable.
 */
function _formatAmaReference(
  num: number,
  filename: string,
  page: string,
  doc?: ExportDocument
): string {
  const pageStr = _cleanPage(page);
  if (!doc || (!doc.author && !doc.title && !doc.year && !doc.sourceOrganisation)) {
    return `${num}. ${filename}${pageStr ? `. p. ${pageStr}` : ""}`;
  }
  let ref = `${num}.`;
  if (doc.author)             ref += ` ${doc.author}.`;
  if (doc.title)              ref += ` ${doc.title}.`;
  if (doc.sourceOrganisation && doc.year) {
    ref += ` ${doc.sourceOrganisation}; ${doc.year}.`;
  } else if (doc.sourceOrganisation) {
    ref += ` ${doc.sourceOrganisation}.`;
  } else if (doc.year) {
    ref += ` ${doc.year}.`;
  }
  if (pageStr) ref += ` p. ${pageStr}.`;
  return ref;
}

function _htmlEscape(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
